import {useEffect, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {
    fetchCurrentFormColumns,
    fetchFields,
    fetchScreenSearch,
    getFormData,
    saveSearch
} from './useAppSearchesQuery';

import {buildQueryFilters, buildSearchFilters, getFormtitle, serializeFields} from './utils';
import {endForm} from '../../services/form/form';
import {ISearchGeneral, ISearchQuery, SEARCH_INIT} from './types/search';
import {ISearchField} from '../../common/searchType';
import {ISearchCondition} from './types/filters';
import * as messageActions from '../../_shared/Messages/actions/MessageActions';
import messagesText from '../../_shared/Messages/texts';
import {defaultOperators} from './types/operator';
import {Operator} from '@priority-software/priority-utils/lib/operators';
import {ColumnType, getBaseColumnType} from './types/column';
import {setIsReactPopupOpen} from '../../_shared/GWTBridge/invoke/netForm';
import {setIsNavigationEnabled} from '../../_shared/GWTBridge/invoke/genPage';
import {isDevEnvProcess} from '../../utils/platform';

const FIELD_QUERY_KEY = 'fields';
const SEARCH_QUERY_KEY = 'search';
const FORM_COLUMNS_QUERY_KEY = 'formColumns';

export const useAppsearches = () => {
    const [search, setSearch] = useState<ISearchGeneral>(SEARCH_INIT);
    const [filters, setFilters] = useState<ISearchCondition[]>([]);
    const [fields, setFields] = useState<ISearchField[]>([]);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState(false);
    const [formSession, setFormSession] = useState('');
    const [currentFormNAme, setCurrentFormName] = useState('');

    const queryClient = useQueryClient();
    const handleError = err => {
        handleClose();
        queryClient.cancelQueries(SEARCH_QUERY_KEY);
        queryClient.cancelQueries(FIELD_QUERY_KEY);
        queryClient.cancelQueries([FORM_COLUMNS_QUERY_KEY, currentFormNAme]);
        isDevEnvProcess() && console.log({err});
    };

    const {data: formData, isFetching: isLoadingCurrentForm} = useQuery(
        [FORM_COLUMNS_QUERY_KEY, currentFormNAme],
        fetchCurrentFormColumns,
        {
            enabled: isOpen,
            onError: handleError
        }
    );

    const {isFetching: isFetchingSearch, data: searchData} = useQuery(
        [SEARCH_QUERY_KEY],
        fetchScreenSearch,
        {
            enabled: isOpen,
            onSuccess: data => {
                setSearch(data.general);
            },
            onError: handleError
        }
    );

    //should be cached??
    const {isFetching: isFetchingFields, data: fieldsNames} = useQuery(
        [FIELD_QUERY_KEY],
        fetchFields,
        {
            enabled: isOpen,
            onError: handleError
        }
    );

    const {mutate: handleSave, isLoading: isLoadingUpdate} = useMutation(() => onSaveSearch(), {
        onSuccess: () => {
            handleClose();
        },
        onError: err => {
            console.log('err', err);
        }
    });

    const isLoading =
        isLoadingUpdate || isFetchingSearch || isFetchingFields || isLoadingCurrentForm;

    useEffect(() => {
        if (formData?.columns?.length && fieldsNames?.length && searchData && !isLoading) {
            const {columns} = formData;
            const fieldsData = serializeFields(fieldsNames, columns);
            const searchFilters = buildSearchFilters(searchData.filters, fieldsData);

            setFilters(searchFilters);
            setFields(fieldsData);
        }

        if (!isLoading && formData?.formSession) {
            setFormSession(formData.formSession);
        }
    }, [fieldsNames, formData, searchData, isLoading]);

    useEffect(() => {
        setIsReactPopupOpen(isOpen);
        setIsNavigationEnabled(!isOpen);
    }, [isOpen]);

    const handleSetDirty = () => {
        !isDirty && setIsDirty(true);
    };

    const onSaveSearch = () => {
        const queryFilters = buildQueryFilters(filters, search.ignoreCase);

        const name = search.name ? search.name : queryFilters?.length > 0 ? getFormtitle() : '';
        const newSearch: ISearchQuery = {
            general: {
                ...search,
                name
            },
            filters: queryFilters
        };
        return saveSearch(newSearch);
    };

    const handleAddFilter = (field: ISearchField) => {
        const {columnID, title, type, isZoom, columnName} = field;
        if (!filters.some(filter => filter.columnID === columnID)) {
            const baseType = getBaseColumnType(type);
            const operator =
                isZoom && baseType === ColumnType.Text
                    ? Operator.Equals
                    : defaultOperators[baseType];
            const newFilter: ISearchCondition = {
                title,
                columnID,
                columnName,
                ignoreCase: false,
                isdesc: false,
                isSort: false,
                type: baseType,
                origType: type,
                operator,
                values: []
            };
            setFilters(prev => [...prev, newFilter]);
            handleSetDirty();
        }
    };

    const handleUpdateFilter = (colID: number | Long, filter: ISearchCondition) => {
        setFilters(prev => {
            const filterToUpdateIndex = prev.findIndex(fltr => fltr.columnID === colID);
            if (filterToUpdateIndex > -1) {
                const updatedFilters = [...prev];
                updatedFilters[filterToUpdateIndex] = filter;
                return updatedFilters;
            }
            return prev;
        });

        handleSetDirty();
    };

    const onUpdateFiltersOrder = (orderedFilters: ISearchCondition[]) => {
        setFilters([...orderedFilters]);
        handleSetDirty();
    };

    const handleRemoveFilter = (columnID: number | Long) => {
        setFilters(prev => {
            return [...prev].filter(filter => filter.columnID !== columnID);
        });
        handleSetDirty();
    };

    const handleChangeName = (name: string) => {
        setSearch(prev => ({...prev, name}));
        handleSetDirty();
    };

    const handleChangeDefault = () => {
        setSearch(prev => ({
            ...prev,
            isDefault: !prev.isDefault,
            isAbsoluteDefault: prev.isDefault ? false : prev.isAbsoluteDefault
        }));
        handleSetDirty();
    };

    const handleChangeDefaultAbsolute = () => {
        setSearch(prev => ({...prev, isAbsoluteDefault: !prev.isAbsoluteDefault}));
        handleSetDirty();
    };

    const handleUpdateIgnoreCase = () => {
        setSearch(prev => ({...prev, ignoreCase: !prev.ignoreCase}));
        handleSetDirty();
    };

    const onClose = () => {
        if (isDirty) {
            messageActions.showConfirmMessage({
                title: messagesText.justMoment,
                content: messagesText.searchStopsShouldExit,
                confirmButtonText: messagesText.yesExitSearch,
                cancleButtonText: messagesText.cancel,
                defaultButton: 0,
                onConfirm: () => handleClose()
            });
        } else {
            handleClose();
        }
    };
    const handleClose = async () => {
        setIsOpen(false);
        formSession && (await endForm(formSession));
        handleClear();
        setIsDirty(false);
        setFields([]);
        setFormSession('');
    };

    const handleOpen = () => {
        const {screenName, screenKey} = getFormData();
        setCurrentFormName(`${screenName}${screenKey}`);
        setIsOpen(true);
    };

    const handleClear = () => {
        setFilters([]);
        setSearch(SEARCH_INIT);
        setIsDirty(true);
    };

    const handleCancelQueries = () => {
        queryClient.cancelQueries([FORM_COLUMNS_QUERY_KEY, SEARCH_QUERY_KEY, FIELD_QUERY_KEY]);
        handleClose();
    };

    const getColumnByID = (id: number | Long): ISearchField =>
        fields.find(fld => fld.columnID === id);

    return {
        search,
        filters,
        fields,
        isOpen,
        isLoading,
        formSession,
        isDirty,
        handleOpen,
        handleChangeName,
        handleChangeDefault,
        handleChangeDefaultAbsolute,
        handleUpdateIgnoreCase,
        handleAddFilter,
        handleRemoveFilter,
        handleSave,
        handleClose: onClose,
        handleClear,
        handleUpdateFilter,
        handleCancelQueries,
        onUpdateFiltersOrder,
        getColumnByID
    };
};
